// Growth (Health module) and School (0032). English counterpart of groeiSchoolApp.nl.ts.
export const appGrowthEn = {
    title: 'Growth',
    intro: "Your children's height and weight, calmly on a growth chart. Only parents see this. Not on the tablet, unless a parent explicitly confirms that themselves for a tablet; it can always be taken off again.",
    tab: 'Growth',
    empty: 'No measurements yet. Measure calmly, for example after a visit to the child health clinic.',
    measurements: '{count} measurements',
    measurementsOne: '1 measurement',
    lastOn: 'Last measured on {date}',
    open: 'Growth of {name}',
    noConsentYet: 'Not turned on yet',
    noMeasurements: 'No measurements yet',
    consent: {
        version: '2026-10-08.1',
        title: 'Track growth for {name}?',
        parental: {
            text: "Height and weight are health data. We only keep them to show {name}'s growth on a chart.\nIf you pick a reference group for the growth chart (children of Turkish, Moroccan or Hindustani (Surinamese) origin in the Netherlands), we keep that choice too. It says something about origin.\nIf {name} was born early, you can enter the length of the pregnancy at birth. That is health data too.\nOnly the parents in this family see it. Never for children and never for linked family. Not on the tablet, unless a parent explicitly confirms that themselves for a tablet; it can always be taken off again.\nYou can download everything. Withdrawing deletes all of {name}'s measurements and choices right away; backups lose it within 90 days.\nThe lines are an aid, not a judgement or a diagnosis. In doubt, ask the child health clinic.\nAMUMIN B.V. is responsible for this data. Your rights are in the privacy statement.",
            check: "I am a parent of {name} ({name} is under 16) and I consent to keeping {name}'s height, weight and growth chart choices.",
        },
        self: {
            text: 'Height and weight are health data. We only keep them to show your growth on a chart.\nIf a reference group is picked for your growth chart (for example Turkish, Moroccan or Hindustani (Surinamese) origin in the Netherlands), we keep that choice too. It says something about origin. The same goes for the length of the pregnancy at your birth if you were born early: that is health data.\nOnly the parents in this family see it (so the other parent too). Never for children and never for linked family. Not on the tablet, unless a parent explicitly confirms that themselves for a tablet; it can always be taken off again.\nYou can download everything. Withdrawing deletes all your measurements and choices right away; backups lose it within 90 days.\nAMUMIN B.V. is responsible for this data. Your rights are in the privacy statement.',
            check: 'I consent to keeping my own height, weight and growth chart choices.',
        },
        /**
         * Aanvulling (0051, tekst 2026-09-28.1): gevraagd op het moment dat een ouder een keuze maakt
         * die een oudere toestemming niet noemde (hs_nl, zwangerschapsduur; bij `self` elke groep
         * behalve nl). De aanvulling noemt altijd ALLE keuzes die die tekst toevoegt (groep en
         * zwangerschapsduur), ook als de ouder er nu maar één kiest: dan dekt ze ze ook echt (review
         * gpl-reg r1). Hoort bij dezelfde hash als parental en self.
         */
        extension: {
            title: 'Consent for these choices',
            intro: 'You already gave consent for Growth for {name}. The text back then did not yet mention everything you can choose now. So we ask separately for consent for these two choices, even if you pick only one of them now:',
            group: 'A reference group for the lines, such as Turkish, Moroccan or Hindustani (Surinamese) origin in the Netherlands. That choice says something about origin: it is a special category of personal data.',
            gestation: 'The length of the pregnancy at birth, if {name} was born early (22 to 36 weeks). That is health data.',
            rest: 'We only use it for the lines on the chart. Only the parents in this family see it. Not on the tablet, unless a parent explicitly confirms that themselves for a tablet; it can always be taken off again. Withdrawing deletes it right away, together with the measurements. The lines are not a judgement or a diagnosis.\nWith this consent, the newest text of the Growth consent applies to {name}.',
            check: 'I consent to keeping these choices for {name}.',
            selfText: 'Your parents can pick a reference group for your growth chart (for example Turkish, Moroccan or Hindustani (Surinamese) origin in the Netherlands). That says something about origin. If you were born early, they can also enter the length of the pregnancy at your birth: that is health data. Both need your consent.',
            selfCheck: 'I consent to keeping these choices for my own growth chart.',
            give: 'Give consent and save',
            giveSelf: 'Give consent',
            needsOwn: '{name} decides this personally: {name} can allow it under Growth in their own app.',
            selfTitle: 'Your consent, with the new text',
            extended: 'Updated on {date} with the newest text.',
        },
        give: 'Give consent',
        privacyLink: 'Read the privacy statement',
        notNow: 'Not now',
        needsOwn: 'Only {name} can consent for {name}, with their own login.',
        needsOwnTeen: 'From 16, {name} decides. With their own login, {name} can give consent under Growth; after that you can add the measurements.',
        lapsed: '{name} is now 16 and decides for themselves. You can still view, download or delete the measurements, but not add new ones.',
        given: 'Consent given on {date}',
        givenSelf: 'You consented on {date}',
        givenByThem: '{name} consented on {date}',
        withdraw: 'Withdraw consent and delete everything',
        withdrawTitle: 'Withdraw consent?',
        withdrawBody: "All of {name}'s measurements are deleted right away, and the growth chart choice too. This cannot be undone.",
        withdrawConfirm: 'Withdraw and delete',
        withdrawn: "Consent withdrawn. {name}'s measurements are deleted.",
    },
    add: 'Add measurement',
    edit: 'Edit measurement',
    date: 'Date',
    length: 'Height (cm)',
    weight: 'Weight (kg)',
    lengthShort: 'Height',
    weightShort: 'Weight',
    bmi: 'BMI',
    wflShort: 'Weight/height',
    note: 'Note (optional)',
    notePlaceholder: 'For example: measured at the clinic',
    oneOfBoth: 'Fill in height, weight or both.',
    saved: 'Measurement saved',
    deleted: 'Measurement deleted',
    deleteConfirm: 'Delete this measurement?',
    errors: {
        empty: 'Fill in height, weight or both.',
        length_range: 'That height looks wrong. Use centimetres, for example 123.5.',
        weight_range: 'That weight looks wrong. Use kilograms, for example 24.5.',
        future: 'That date is in the future.',
        before_birth: 'That date is before the date of birth.',
        note_long: 'The note is too long (200 characters at most).',
        format: 'Use at most one digit after the decimal point.',
    },
    chart: {
        length: 'Height for age',
        weight: 'Weight for age',
        bmi: 'BMI for age',
        wfl: 'Weight for height',
        bmiFrom2: 'We show BMI from 2 years.',
        age: 'Age (years)',
        date: 'Date',
        median: 'Middle (average)',
        sd1: '−1 and +1 SD',
        sd2: '−2 and +2 SD',
        you: '{name}',
        lastPoint: 'Last measurement: {value} on {date}',
        info: 'The lines are an aid, not a judgement or a diagnosis. In doubt, ask the child health clinic.',
        gaps: {
            no_sex: 'Choose boy or girl below to see the growth lines.',
            no_group: 'Choose a reference group below to see the growth lines. Without one you see only the measurements.',
            no_birth_date: 'Fill in the date of birth to see growth by age.',
        },
        source: 'Source of the lines: {source}',
        sources: {
            schonbeck_2011: 'Dutch children, 2009 ({cite})',
            tno_2010: 'Dutch children, 2009 ({cite})',
            tno_2010_origin: 'Children of {group} in the Netherlands, 2009 ({cite})',
            fredriks_2000: 'Dutch children, 1997 ({cite})',
            fredriks_2003: 'Children of {group} in the Netherlands, 1997 ({cite})',
            fredriks_2004: 'Children of {group} in the Netherlands, 1997 ({cite})',
            de_wilde_2014: 'Children of {group} in the Netherlands, 2010 ({cite})',
            de_wilde_2013: 'Children of {group} in the Netherlands, 1976 ({cite})',
            bocca_2012: 'Children born after {weeks} weeks of pregnancy ({cite})',
            bocca_2012_wfh: 'Children born too early ({cite})',
        },
        citations: {
            schonbeck_2011: 'Schönbeck et al., PLoS ONE 2011, CC BY',
            tno_2010: 'TNO, Groeidiagrammen 2010',
            tno_2010_origin: 'TNO, Groeidiagrammen 2010',
            fredriks_2000: 'TNO, Fredriks et al. 2000',
            fredriks_2003: 'TNO, Fredriks et al. 2003',
            fredriks_2004: 'TNO, Fredriks et al. 2004',
            de_wilde_2014: 'TNO, de Wilde et al. 2014',
            de_wilde_2013: 'TNO, de Wilde et al. 2013',
            bocca_2012: 'TNO, Bocca-Tjeertes et al. 2012',
            bocca_2012_wfh: 'TNO, Bocca-Tjeertes et al. 2012',
        },
        /** Golf 19: welke lijnen waar gelden (een tabel per stuk, zie referenceSegments). */
        rangeUntil: 'up to {age} years:',
        rangeBetween: '{from} to {to} years:',
        rangeFrom: 'from {age} years:',
        sourceJoin: '; ',
        switchNote: 'At {age} years the lines move to another table (see the source below). They may jump a little there.',
        pretermNote: 'Up to {age} years you see the lines for children born after {weeks} weeks of pregnancy, by age since birth. After that, the usual lines.',
        pretermWflNote: 'Up to {age} years you see the lines for children born too early here. After that, the usual lines.',
        table: 'Table with all measurements',
        showTable: 'Show as table',
        showChart: 'Show as chart',
    },
    settings: {
        title: 'Growth chart',
        sex: 'Sex (for the growth lines)',
        sexUnset: 'Not chosen',
        female: 'Girl',
        male: 'Boy',
        group: 'Reference group',
        groupHint: 'Only for the lines on the chart. Choose what fits best; you can change it or leave it empty at any time.',
        groups: {
            none: 'None (measurements only)',
            nl: 'Dutch children',
            tr_nl: 'Children of Turkish origin in the Netherlands',
            ma_nl: 'Children of Moroccan origin in the Netherlands',
            hs_nl: 'Children of Hindustani (Surinamese) origin in the Netherlands',
        },
        groupNames: {
            nl: 'Dutch',
            tr_nl: 'Turkish origin',
            ma_nl: 'Moroccan origin',
            hs_nl: 'Hindustani (Surinamese) origin',
        },
        /** Golf 19: neutrale uitleg bij de groepen en de zwangerschapsduur (alleen te vroeg). */
        groupInfo: 'The lines for each group come from Dutch research among children of that origin. Hindustani (Surinamese) means children with South Asian ancestors, often by way of Suriname. Not every group has its own lines for every measure; below the chart you can see which lines are shown.',
        gestation: 'Length of pregnancy at birth',
        gestationNone: 'Born on time (or not filled in)',
        gestationWeeks: '{weeks} weeks',
        gestationHint: 'Only fill this in if {name} was born too early (before {term} weeks). Up to {age} years you then see the lines for children born too early; for {min} to {max} weeks those are the lines for {table} weeks. This is health data: it falls under the same consent as the measurements, only parents see it, and withdrawing erases it too.',
        saved: 'Saved',
    },
    birthDate: 'Date of birth of {name}',
    birthDateHint: 'Needed for growth by age. Also shown under Birthdays.',
    download: 'Download',
    downloadCsv: 'As a table (CSV)',
    downloadJson: 'Everything (JSON)',
    clear: 'Delete all measurements',
    clearTitle: "Delete all of {name}'s measurements?",
    clearBody: 'Consent stays, so you can start again. This cannot be undone.',
    cleared: "All of {name}'s measurements are deleted.",
    privacyNote: 'Only parents see this. Not on the tablet, unless a parent explicitly confirms that themselves for a tablet; it can always be taken off again.',
    selfIntro: 'Your height and weight, calmly in a growth chart. Your parents add the measurements; only you and your parents see this.',
    selfUnder16: 'Your parents keep track of your height and weight. From 16, you decide this yourself.',
    selfNote: 'Only you and your parents see this. Not on the tablet, unless a parent explicitly confirms that themselves for a tablet; it can always be taken off again.',
    /** Kolomkoppen van de CSV-download (review g3-fs r1: in de taal van het gezin). */
    csvHead: ['date', 'length_cm', 'weight_kg', 'bmi', 'note'],
    moduleOff: 'Growth belongs to the Health module. It is off.',
};
export const appSchoolEn = {
    title: 'School',
    intro: 'Report grades and tests per child, calmly on a timeline. Only parents see this; never on the tablet.',
    childIntro: 'Your reports and tests. Your parents fill this in.',
    childOff: 'Your parents have not turned this on for you yet.',
    tab: 'School',
    open: 'School of {name}',
    noChildren: 'There are no children in your family yet.',
    empty: 'Nothing filled in yet. Start with the latest report or a test.',
    nothingYet: 'Nothing filled in yet',
    count: '{count} results',
    countOne: '1 result',
    add: 'Add result',
    edit: 'Edit result',
    saved: 'Saved',
    deleted: 'Deleted',
    deleteConfirm: 'Delete this result?',
    timeline: 'Timeline',
    trends: 'Per subject',
    noTrend: 'A line appears when there are two or more results.',
    childView: 'May {name} see this?',
    childViewHint: 'Only with their own login in the web app, only their own results and read-only. Never on the tablet.',
    childViewOn: '{name} can now see this.',
    childViewOff: '{name} does not see this.',
    download: 'Download',
    downloadCsv: 'As a table (CSV)',
    downloadJson: 'Everything (JSON)',
    clear: 'Delete everything of {name}',
    clearTitle: "Delete all of {name}'s results?",
    clearBody: 'This cannot be undone.',
    cleared: "All of {name}'s results are deleted.",
    field: {
        kind: 'Kind',
        schoolYear: 'School year',
        period: 'Moment',
        date: 'Date (optional)',
        group: 'Year group',
        groupNone: 'Unknown',
        subject: 'Subject',
        domain: 'Part',
        scale: 'Scale',
        grade: 'Grade',
        gradeHint: 'From 1 to 10, for example 7.5',
        word: 'Assessment',
        level: 'Level',
        score: 'Skill score (optional)',
        dmtScore: 'Score (words read correctly)',
        provider: 'Test by (optional)',
        note: 'Note (optional)',
    },
    errors: {
        school_year: 'Choose a school year.',
        period: 'Choose a moment that fits this kind.',
        group: 'Choose a year group from 1 to 8.',
        subject: 'Choose a subject.',
        domain: 'Choose a part.',
        scale: 'Choose a scale.',
        value: 'This value does not fit the scale.',
        provider: 'Choose a test from the list.',
        note: 'The note is too long (200 characters at most).',
        future: 'That date is in the future.',
        subject_dup: 'This subject is already there.',
    },
    /** Titels van een lijn "Per vak" (review g3-ux r1): rapport en toets nooit dezelfde titel. */
    titles: {
        grade: '{subject} · report',
        lvs: 'Test · {domain}',
    },
    kinds: {
        grade: 'Report grade',
        lvs: 'Test (pupil monitoring system)',
        avi: 'AVI reading level',
        dmt: 'DMT (three-minute test)',
        level: 'Working level',
        doorstroom: 'Year 8 transition test',
        advies: 'School advice',
    },
    periods: {
        b: 'Start',
        r1: 'Report 1',
        m: 'Middle',
        r2: 'Report 2',
        r3: 'Report 3',
        e: 'End',
        r4: 'Report 4',
        voorlopig: 'Provisional',
        definitief: 'Final',
    },
    periodsShort: {
        b: 'S',
        r1: 'R1',
        m: 'M',
        r2: 'R2',
        r3: 'R3',
        e: 'E',
        r4: 'R4',
        voorlopig: 'prov.',
        definitief: 'final',
    },
    scales: {
        number: 'Grade 1-10',
        word: 'Words (O to ZG)',
        roman: 'I to V',
        letter: 'A to E',
        ref: '1F / 2F / 1S',
    },
    domains: {
        rekenen: 'Maths',
        begrijpend_lezen: 'Reading comprehension',
        spelling: 'Spelling',
        technisch_lezen: 'Reading fluency',
        lezen: 'Reading',
        taalverzorging: 'Language use',
    },
    providers: {
        cito: 'Cito (Leerling in beeld)',
        boom: 'Boom LVS',
        iep: 'IEP',
        dia: 'Diatoetsen',
        amn: 'AMN',
        route8: 'ROUTE 8',
        doe: 'DOE test',
        other: 'Other',
    },
    words: {
        O: 'Insufficient',
        M: 'Moderate',
        V: 'Sufficient',
        RV: 'More than sufficient',
        G: 'Good',
        ZG: 'Very good',
    },
    advice: {
        pro: 'Practical education',
        vmbo_b: 'vmbo basic',
        vmbo_b_k: 'vmbo basic / kader',
        vmbo_k: 'vmbo kader',
        vmbo_k_gt: 'vmbo kader / gt',
        vmbo_gt: 'vmbo mixed/theoretical (gt)',
        vmbo_gt_havo: 'vmbo gt / havo',
        havo: 'havo',
        havo_vwo: 'havo / vwo',
        vwo: 'vwo',
    },
    explain: {
        grade: 'A report grade from 1 to 10. 5.5 or higher is a pass.',
        word: '{code} = {word}.',
        aviStart: 'AVI-Start = the start of learning to read, in year group 3.',
        aviPlus: 'AVI-Plus = the highest AVI level, beyond the end of year group 7.',
        avi: 'AVI {code} = the reading level of the {moment} of year group {group}.',
        level: '{code} = the level of the {moment} of year group {group}.',
        roman: {
            I: 'I = among the top 20% of children in the Netherlands.',
            II: 'II = just above average (the next 20%).',
            III: 'III = around the average (the middle 20%).',
            IV: 'IV = just below average (the next 20%).',
            V: 'V = among the lowest 20%. The school then looks at what extra helps.',
        },
        letter: {
            A: 'A = among the top 25% of children in the Netherlands.',
            B: 'B = just above average (the next 25%).',
            C: 'C = just below average (the next 25%).',
            D: 'D = well below average (the next 15%).',
            E: 'E = among the lowest 10%. The school then looks at what extra helps.',
        },
        ref: {
            '<1F': '1F not reached yet. That is the basic level for the end of year group 8.',
            '1F': '1F = the basic level almost all children reach at the end of year group 8.',
            '2F': '2F = the target level for reading and language at the end of year group 8.',
            '1S': '1S = the target level for maths at the end of year group 8.',
        },
        dmt: 'The DMT measures how many single words a child reads correctly in three minutes.',
        doorstroom: 'The transition test (since 2024) measures reading, language use and maths in 1F, 2F and 1S.',
        advies: 'The school advice says which type of secondary school fits.',
        moments: { B: 'start', M: 'middle', E: 'end' },
    },
    subjects: {
        title: 'Subjects',
        hint: "The subjects on your school's report. You can rename or remove them; old grades stay.",
        add: 'Add subject',
        name: 'Subject name',
        suggested: 'Add standard subjects',
        suggestions: [
            'Maths',
            'Language',
            'Spelling',
            'Reading',
            'Reading comprehension',
            'Writing',
            'Geography',
            'History',
            'Nature',
            'English',
        ],
        remove: 'Remove',
        rename: 'Rename',
        removed: 'Subject removed. Old grades stay.',
        none: 'No subjects yet. Add them or pick the standard subjects.',
        archived: '{name} (removed)',
    },
    /** Kolomkoppen van de CSV-download (review g3-fs r1: in de taal van het gezin). */
    csvHead: [
        'school_year',
        'period',
        'date',
        'group',
        'kind',
        'subject_or_domain',
        'value',
        'test',
        'note',
    ],
    moduleOff: 'School is off.',
};
//# sourceMappingURL=groeiSchoolApp.en.js.map